import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { amsterdamDay } from '../utils/activity';

const TODAY = () => amsterdamDay();

// Sessietoken uit localStorage (gezet door de AuthProvider bij inloggen).
export const AUTH_TOKEN_KEY = 'authToken';

// fetch met het sessietoken als bearer (indien ingelogd). Voor publieke endpoints
// is de extra header onschadelijk.
//
// LET OP: we gebruiken `X-Authorization` i.p.v. `Authorization`. Azure Static Web
// Apps met managed functions overschrijft de standaard `Authorization`-header met
// zijn eigen Easy-Auth-token, waardoor ons sessietoken de functie nooit bereikt.
// De backend leest daarom `X-Authorization` (zie shared/userAuth.js bearerToken).
export async function authFetch(url, opts = {}) {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const headers = { ...(opts.headers || {}) };
  if (token) headers['X-Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...opts, headers });
}

// Kleine helper voor schrijf-acties (claim/report/notities). Geeft
// { ok, status, data } terug; gooit niet bij een niet-2xx-status.
export async function postJson(url, body, method = 'POST') {
  const res = await authFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// Start een OAuth-koppeling (Strava/Polar/Garmin). Het init-endpoint vereist een
// ingelogde eigenaar en geeft de autorisatie-URL als JSON terug; we halen die op
// mét het sessietoken (X-Authorization) en navigeren er daarna zelf naartoe. Een
// gewone <a href>-navigatie zou de header niet kunnen meesturen.
export async function oauthConnect(url) {
  const res  = await authFetch(url);
  const data = await res.json().catch(() => ({}));
  if (res.ok && data.url) {
    window.location.href = data.url;
    return { ok: true };
  }
  return { ok: false, error: data.error || 'Koppelen mislukt. Ben je ingelogd en eigenaar van deze transponder?' };
}

// Backed by TanStack Query. Keeps the original { data, loading, error } shape
// (plus refetch) so all ~35 hooks and their consumers work unchanged, while
// gaining request de-duplication, caching and invalidation for free.
//
// Behaviour preserved from the old hand-rolled version:
//   - url === null  → skip the fetch entirely (enabled: false), data === null
//   - refreshMs > 0 → poll on that interval, paused while the tab is hidden
//     (refetchIntervalInBackground stays false) and refreshed on window focus.
//   - loading is only true on the first load, not on background polls.
function useFetch(url, refreshMs = 0) {
  const query = useQuery({
    queryKey: [url],
    enabled: !!url,
    // staleTime 0 keeps "fresh on mount" like before; the cache still de-dupes
    // concurrent identical requests and survives quick remounts.
    staleTime: 0,
    refetchInterval: refreshMs > 0 ? refreshMs : false,
    refetchIntervalInBackground: false,
    queryFn: async ({ signal }) => {
      const r = await authFetch(url, { signal });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });

  return {
    data: url ? (query.data ?? null) : null,
    loading: query.isLoading,
    error: query.error ? query.error.message : null,
    refetch: query.refetch,
    updatedAt: query.dataUpdatedAt,
    fetching: query.isFetching,
  };
}

// Invalidate one or more endpoints so dependent hooks refetch — the modern
// replacement for the `window.location.reload()` crutch after write actions.
// Pass the same url prefix(es) used by the hooks, e.g. '/api/coach-links'.
export function useInvalidate() {
  const qc = useQueryClient();
  return (...urls) =>
    Promise.all(
      urls.map((u) =>
        qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith(u) })
      )
    );
}

const LIVE_MS = 10_000;

export function useDays() {
  return useFetch('/api/days');
}

export function useSessions(date) {
  const url       = date ? `/api/sessions?date=${date}` : null;
  const refreshMs = date === TODAY() ? LIVE_MS : 0;
  return useFetch(url, refreshMs);
}

export function usePassings(transponder, date) {
  const url       = transponder && date
    ? `/api/passings?transponder=${encodeURIComponent(transponder)}&date=${date}`
    : null;
  const refreshMs = date === TODAY() ? LIVE_MS : 0;
  return useFetch(url, refreshMs);
}

export function useSeasonPassings(transponder, from, to) {
  const url = transponder && from && to
    ? `/api/passings?transponder=${encodeURIComponent(transponder)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    : null;
  return useFetch(url);
}

export function useStravaStatus(transponder) {
  const url = transponder
    ? `/api/strava-status?transponder=${encodeURIComponent(transponder)}`
    : null;
  return useFetch(url);
}

export function useStravaHr(transponder, date) {
  const url = transponder && date
    ? `/api/strava-hr?transponder=${encodeURIComponent(transponder)}&date=${date}`
    : null;
  return useFetch(url);
}

// Live-opgeslagen HR (door de app(s) tijdens de sessie) — zelfde vorm als useStravaHr.
export function useSessionHr(transponder, date, pollInterval = 0) {
  const url = transponder && date
    ? `/api/session-hr?transponder=${encodeURIComponent(transponder)}&date=${date}`
    : null;
  return useFetch(url, pollInterval);
}

export function useSessionNotes(transponder, date) {
  const url = transponder && date
    ? `/api/session-notes?transponder=${encodeURIComponent(transponder)}&date=${date}`
    : null;
  return useFetch(url);
}

export function useSeasonBest(transponder, from, to) {
  const url = transponder && from && to
    ? `/api/season-best?transponder=${encodeURIComponent(transponder)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    : null;
  return useFetch(url);
}

export function useStravaSettings(transponder) {
  const url = transponder
    ? `/api/strava-settings?transponder=${encodeURIComponent(transponder)}`
    : null;
  return useFetch(url);
}

export function useSeasonList(transponder) {
  const url = transponder
    ? `/api/season-list?transponder=${encodeURIComponent(transponder)}`
    : null;
  return useFetch(url);
}

export function useSessionStats(date) {
  const url       = date ? `/api/session-stats?date=${date}` : null;
  const refreshMs = date === TODAY() ? LIVE_MS : 0;
  return useFetch(url, refreshMs);
}

export function useAdminMe() {
  const [state, setState] = useState({ loading: true, role: null, email: null, httpStatus: null });
  useEffect(() => {
    fetch('/api/mgmt-me')
      .then(async r => {
        const d = await r.json().catch(() => ({}));
        setState({ loading: false, role: d.role ?? null, email: d.email ?? null, httpStatus: r.status });
      })
      .catch(() => setState({ loading: false, role: null, email: null, httpStatus: 0 }));
  }, []);
  return state;
}

export function useAdminFeedback(enabled) {
  return useFetch(enabled ? '/api/mgmt-feedback' : null);
}

// Altijd (niet alleen als het tabblad open staat) actief voor admins, zodat de
// "Meldingen"-tab in AdminView een live badge met het aantal open meldingen kan
// tonen zonder dat je hoeft te klikken.
export function useAdminOpenReports(enabled) {
  return useFetch(enabled ? '/api/mgmt-reports?status=open' : null);
}

export function useAdminStats(date) {
  // date=null → skip fetch (not unlocked), date=undefined → overview, date='YYYY-MM-DD' → detail
  const url = date === null
    ? null
    : date
      ? `/api/mgmt-stats?date=${date}`
      : '/api/mgmt-stats';
  return useFetch(url);
}

export function useAdminConfig(enabled) {
  return useFetch(enabled ? '/api/mgmt-config' : null);
}

export function useCoachLinks(enabled = true) {
  return useFetch(enabled ? '/api/coach-links' : null);
}

export function useLiveHeartRates(enabled = true) {
  return useFetch(enabled ? '/api/live-hr' : null, 5000);
}

export function useNickname(transponder) {
  const url = transponder
    ? `/api/transponder-nickname?transponder=${encodeURIComponent(transponder)}`
    : null;
  return useFetch(url);
}

export function useProfileStats(transponder) {
  const url = transponder
    ? `/api/profile-stats?transponder=${encodeURIComponent(transponder)}`
    : null;
  return useFetch(url);
}

export function useMostLaps(from, to) {
  const url = from && to
    ? `/api/most-laps?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    : null;
  return useFetch(url);
}

export function useBestLaps(from, to, n) {
  const url = from && to && n
    ? `/api/best-laps?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&n=${n}`
    : null;
  return useFetch(url);
}

// Vooruitgangsranglijst: grootste verbetering van de beste ronde in de periode.
export function usePrProgress(from, to) {
  const url = from && to
    ? `/api/pr-progress?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    : null;
  return useFetch(url);
}

// Schaatsers die de ingelogde gebruiker volgt (of null wanneer uitgelogd).
export function useFollows(enabled = true) {
  return useFetch(enabled ? '/api/follows' : null);
}

// Activiteitenfeed van gevolgde schaatsers.
export function useFollowFeed(enabled = true) {
  return useFetch(enabled ? '/api/follow-feed' : null);
}

// Live-standen via Azure Web PubSub. Vraagt een client-access-URL op bij
// /api/live-negotiate, opent een WebSocket en levert elke gepushte stand op.
// Valt stil terug (connected=false) wanneer Web PubSub niet geconfigureerd is
// of de socket wegvalt; useLivePassings poll't dan verder.
function useLivePush() {
  const [data, setData]           = useState(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    let closed = false;
    let ws = null;
    let retry = null;

    async function connect() {
      if (closed) return;
      try {
        const r = await authFetch('/api/live-negotiate');
        if (!r.ok) throw new Error(`negotiate HTTP ${r.status}`);
        const { url } = await r.json();
        // Geen URL → resource niet geprovisioned; niet reconnecten, poll blijft leidend.
        if (!url || closed) return;

        ws = new WebSocket(url, 'json.webpubsub.azure.v1');
        ws.onopen = () => { if (!closed) setConnected(true); };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === 'message' && msg.data) setData(msg.data);
          } catch { /* niet-JSON systeembericht: negeren */ }
        };
        ws.onclose = () => {
          if (closed) return;
          setConnected(false);
          retry = setTimeout(connect, 5000); // reconnect + nieuwe (verlopen) token
        };
        ws.onerror = () => { try { ws.close(); } catch { /* al dicht */ } };
      } catch {
        if (closed) return;
        setConnected(false);
        retry = setTimeout(connect, 5000);
      }
    }

    connect();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      if (ws) { try { ws.close(); } catch { /* al dicht */ } }
    };
  }, []);

  return { data, connected };
}

// Live-standen: prefereer de Web PubSub-push; poll /api/live-passings zolang er
// nog geen push binnen is (koude start) of de socket weg is. Zelfde { data,
// loading }-vorm als voorheen, dus LiveView hoeft niet te wijzigen.
export function useLivePassings() {
  const push     = useLivePush();
  const havePush = push.connected && push.data != null;
  const poll     = useFetch(havePush ? null : '/api/live-passings', 2500);
  return havePush ? { data: push.data, loading: false } : poll;
}

export function useServiceHeartbeat() {
  return useFetch('/api/service-heartbeat', 60_000);
}

export function usePolarStatus(transponder) {
  const url = transponder
    ? `/api/polar-status?transponder=${encodeURIComponent(transponder)}`
    : null;
  return useFetch(url);
}

export function usePolarHr(transponder, date) {
  const url = transponder && date
    ? `/api/polar-hr?transponder=${encodeURIComponent(transponder)}&date=${date}`
    : null;
  return useFetch(url);
}

export function useGarminStatus(transponder) {
  const url = transponder
    ? `/api/garmin-status?transponder=${encodeURIComponent(transponder)}`
    : null;
  return useFetch(url);
}

export function useGarminHr(transponder, date) {
  const url = transponder && date
    ? `/api/garmin-hr?transponder=${encodeURIComponent(transponder)}&date=${date}`
    : null;
  return useFetch(url);
}

export function useReplayPassings(date) {
  const url = date ? `/api/replay-passings?date=${date}` : null;
  return useFetch(url);
}

// Alkmaar IJsbaan coördinaten
const LAT = 52.632;
const LON = 4.744;

// Weer voor één dag, op één uur. `atIso` is een tijdstip (bv. de eerste
// doorkomst van een sessie); het weer wordt dan op dát klokuur gelezen in plaats
// van op het vaste 18:00 dat hier eerst stond. Zonder `atIso` blijft 18:00 de
// standaard — dat is het spitsuur op de baan.
export function useWeather(date, atIso = null) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  // Uur in lokale (baan)tijd; Open-Meteo levert de reeks al in Europe/Amsterdam.
  const hour = useMemo(() => {
    if (!atIso) return 18;
    const d = new Date(atIso);
    if (isNaN(d)) return 18;
    const h = new Intl.DateTimeFormat('nl-NL', {
      timeZone: 'Europe/Amsterdam', hour: 'numeric', hour12: false,
    }).format(d);
    const n = parseInt(h, 10);
    return isNaN(n) ? 18 : n;
  }, [atIso]);

  useEffect(() => {
    if (!date) { setData(null); setLoading(false); return; }

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const today = new Date().toISOString().slice(0, 10);
    const daysDiff = Math.round(
      (new Date(today + 'T12:00:00') - new Date(date + 'T12:00:00')) / 86400000
    );

    const base = daysDiff > 7
      ? 'https://archive-api.open-meteo.com/v1/archive'
      : 'https://api.open-meteo.com/v1/forecast';

    const params = [
      `latitude=${LAT}`,
      `longitude=${LON}`,
      'hourly=temperature_2m,relative_humidity_2m,surface_pressure,wind_speed_10m,wind_direction_10m',
      'wind_speed_unit=kmh',
      'timezone=Europe%2FAmsterdam',
      `start_date=${date}`,
      `end_date=${date}`,
    ].join('&');

    setLoading(true);
    setData(null);

    fetch(`${base}?${params}`, { signal: ctrl.signal })
      .then(r => (r.ok ? r.json() : null))
      .then(json => {
        if (!json?.hourly?.time) { setLoading(false); return; }

        // Waarde op het gevraagde klokuur (lokale tijd).
        const idx = json.hourly.time.findIndex(t => parseInt(t.slice(11, 13), 10) === hour);
        if (idx === -1) { setLoading(false); return; }

        const val = key => {
          const v = json.hourly[key]?.[idx];
          return v != null && !isNaN(v) ? v : null;
        };

        setData({
          temperature: val('temperature_2m'),
          humidity:    val('relative_humidity_2m'),
          pressure:    val('surface_pressure'),
          windSpeed:   val('wind_speed_10m'),
          windDir:     val('wind_direction_10m'),
        });
        setLoading(false);
      })
      .catch(err => {
        if (err.name !== 'AbortError') { setData(null); setLoading(false); }
      });

    return () => ctrl.abort();
  }, [date, hour]);

  return { data, loading };
}
